import { useMutation, useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"

import { Application } from "@/contexts/ApplicationContext"
import { createDocumentRecord } from "@/services/documentService"
import supabase from "@/supabase"

import { useFileUpload } from "./useFileUpload"

// Types for personal details submission
export interface PersonalDetailsSubmission {
  applicationId: string
  section: "bankDetails" | "nextOfKin" | "taxDetails" | "addressDetails" | "headshot" | "finalForms"
  data: Partial<Application["personalDetails"]>
  files?: {
    bankProof?: File
    headshot?: File
    signature?: string
  }
}

export const usePersonalDetails = () => {
  const queryClient = useQueryClient()

  // Initialize file upload hooks for different document types
  const bankProofUploader = useFileUpload({
    bucket: "application-documents",
    path: "bank-proofs",
  })

  const headshotUploader = useFileUpload({
    bucket: "application-documents",
    path: "headshots",
  })

  // Mutation for submitting personal details
  const submitPersonalDetails = useMutation({
    mutationFn: async ({
      applicationId,
      section,
      data,
      files,
    }: PersonalDetailsSubmission): Promise<{
      success: boolean
      message?: string
      fileUrls?: Record<string, string>
    }> => {
      try {
        // First fetch the application to get the applicant_id
        const { data: applicationData, error: applicationError } = await supabase
          .from("applications")
          .select("*, applicant_id")
          .eq("application_id", applicationId)
          .single()

        if (applicationError || !applicationData) {
          console.error("Error fetching application:", applicationError)
          return {
            success: false,
            message: "Application not found. Please check your application ID.",
          }
        }

        // Handle file uploads if provided
        const fileUrls: Record<string, string> = {}

        // Upload bank proof if provided
        if (files?.bankProof && section === "bankDetails") {
          const uploadResult = await bankProofUploader.uploadFile(
            files.bankProof,
            applicationData.id,
            "bank-proof",
          )

          if (!uploadResult.success) {
            return {
              success: false,
              message: `Failed to upload bank proof: ${uploadResult.message}`,
            }
          }

          fileUrls.bankProof = uploadResult.fileUrl || ""

          // Document record is already created by the uploadFile function
          // through the createDocumentRecord service
        }

        // Upload headshot if provided
        if (files?.headshot && section === "headshot") {
          const uploadResult = await headshotUploader.uploadFile(
            files.headshot,
            applicationData.id,
            "headshot",
          )

          if (!uploadResult.success) {
            return {
              success: false,
              message: `Failed to upload headshot: ${uploadResult.message}`,
            }
          }

          fileUrls.headshot = uploadResult.fileUrl || ""

          // Document record is already created by the uploadFile function
          // through the createDocumentRecord service
        }

        // Save signature as base64 if provided
        if (files?.signature && section === "finalForms") {
          // Use document service to create a record for the signature
          // We'll use a special URL format for the signature
          const signatureUrl = `data:image/png;base64,${files.signature}`

          // Create document record using the service
          const documentResult = await createDocumentRecord(
            applicationData.id,
            "signature",
            signatureUrl,
          )

          if (documentResult.error) {
            console.error("Error saving signature:", documentResult.error)
            return {
              success: false,
              message: "Failed to save signature.",
            }
          }

          fileUrls.signature = signatureUrl
        }

        // Prepare data for applicant update
        const applicantUpdateData: Record<string, string | boolean> = {}

        // Map personal details to database fields based on section
        switch (section) {
          case "bankDetails":
            if (data.bankName) applicantUpdateData.bank_name = data.bankName
            if (data.accountNumber) applicantUpdateData.account_number = data.accountNumber
            if (data.sortCode) applicantUpdateData.sort_code = data.sortCode
            if (data.accountName) applicantUpdateData.account_holder_name = data.accountName
            // No bank_proof_url in the schema, we'll use the documents table instead
            break

          case "nextOfKin":
            if (data.nextOfKin?.name) applicantUpdateData.next_of_kin_name = data.nextOfKin.name
            if (data.nextOfKin?.phone) applicantUpdateData.next_of_kin_phone = data.nextOfKin.phone
            if (data.nextOfKin?.relation)
              applicantUpdateData.next_of_kin_relation = data.nextOfKin.relation
            break

          case "taxDetails":
            if (data.nationalInsuranceNumber) {
              applicantUpdateData.ni_number = data.nationalInsuranceNumber
            }
            if (data.utrNumber) applicantUpdateData.utr_number = data.utrNumber
            if (data.company?.type) applicantUpdateData.company_type = data.company.type
            if (data.company?.number) applicantUpdateData.company_number = data.company.number
            if (data.company?.vatNumber) applicantUpdateData.vat_number = data.company.vatNumber
            break

          case "addressDetails":
            if (data.address?.line1) applicantUpdateData.address_line_1 = data.address.line1
            if (data.address?.line2) applicantUpdateData.address_line_2 = data.address.line2
            if (data.address?.city) applicantUpdateData.city = data.address.city
            if (data.address?.postcode) applicantUpdateData.postcode = data.address.postcode
            break

          case "headshot":
            // No headshot_url in the schema, we'll use the documents table instead
            break

          case "finalForms":
            // These fields don't exist in the schema, so we'll remove them
            break
        }

        // Update the applicant record if we have data to update
        if (Object.keys(applicantUpdateData).length > 0) {
          // Format the current date as 'yyyy-MM-dd HH:mm:ss' to avoid timezone issues
          const formattedTimestamp = format(new Date(), "yyyy-MM-dd HH:mm:ss")

          const { error: applicantUpdateError } = await supabase
            .from("applicants")
            .update({
              ...applicantUpdateData,
              updated_at: formattedTimestamp,
            })
            .eq("id", applicationData.applicant_id)

          if (applicantUpdateError) {
            console.error("Error updating applicant data:", applicantUpdateError)
            return {
              success: false,
              message: "Failed to update personal details.",
            }
          }
        }

        // Invalidate the query to refetch the updated data
        queryClient.invalidateQueries({
          queryKey: ["application", applicationId],
        })

        return {
          success: true,
          message: "Personal details updated successfully",
          fileUrls,
        }
      } catch (error) {
        console.error("Error in submitPersonalDetails:", error)
        return {
          success: false,
          message: "Failed to update personal details. Please try again.",
        }
      }
    },
    onSuccess: (result, variables) => {
      // Invalidate and refetch the application query after a successful mutation
      queryClient.invalidateQueries({
        queryKey: ["application", variables.applicationId],
      })
    },
  })

  return {
    submitPersonalDetails,
    isBankProofUploading: bankProofUploader.isUploading,
    isHeadshotUploading: headshotUploader.isUploading,
  }
}
